import type { EnrollmentFormData } from "@/types/enrollment"
import { memo, useEffect, useId, useMemo } from "react"

import { type SubmitHandler, useForm, useWatch } from "react-hook-form"
import type { Resolver } from "react-hook-form"

import {
  Box,
  Button,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  DialogTitle,
  HStack,
  Portal,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react"

import { ClassSessionsService } from "@/client"
import { Field } from "@/components/atoms/Field"
import ForeignKeySelect from "@/components/molecules/ForeignKeySelect"
import { useClassTemplates } from "@/hooks/api/useClassTemplates"
import { useSessions } from "@/hooks/api/useSessions"
import { useStudents } from "@/hooks/api/useStudents"
import { useQuery } from "@tanstack/react-query"

const skipLinkStyle = {
  position: "absolute",
  left: "-9999px",
  top: "-9999px",
  background: "#3182ce",
  color: "white",
  padding: "8px 16px",
  textDecoration: "none",
  borderRadius: "6px",
  zIndex: 1000,
} as const

// Form only handles enrollment fields (no invoice_data)
type EnrollmentFormValues = EnrollmentFormData

interface EnrollmentFormDialogProps {
  isOpen: boolean
  title: string
  submitLabel: string
  resolver: Resolver<EnrollmentFormValues>
  defaultValues: EnrollmentFormValues
  onSubmit: (values: EnrollmentFormValues) => Promise<void>
  onClose: () => void
  isSubmitting?: boolean
  cancelLabel?: string
  finalFocusRef?: React.RefObject<HTMLElement | null>
  showSkipLink?: boolean
  loadingText?: string
  showPaymentFields?: boolean // Kept for API compat but no longer used
}

const EnrollmentFormDialog = memo(
  ({
    isOpen,
    title,
    submitLabel,
    resolver,
    defaultValues,
    onSubmit,
    onClose,
    isSubmitting = false,
    cancelLabel = "Cancel",
    finalFocusRef,
    showSkipLink = false,
    loadingText,
  }: EnrollmentFormDialogProps) => {
    const formSectionId = useId()

    // Backend already filters students by user permissions!
    // Parents automatically get only their household's students
    const { data: studentsData, isLoading: studentsLoading } = useStudents()
    const { data: classTemplatesData, isLoading: classTemplatesLoading } =
      useClassTemplates()
    const { data: sessionsData, isLoading: sessionsLoading } = useSessions()

    console.log("EnrollmentFormDialog - Students from backend:", {
      students: studentsData?.items,
      count: studentsData?.items?.length,
      isLoading: studentsLoading,
    })

    const normalizedDefaults = useMemo<EnrollmentFormValues>(() => {
      return {
        student_id: defaultValues.student_id ?? "",
        class_template_id: defaultValues.class_template_id ?? "",
        session_id: defaultValues.session_id ?? "",
        status: defaultValues.status ?? null,
        notes: defaultValues.notes ?? null,
      }
    }, [defaultValues])

    const form = useForm<EnrollmentFormValues>({
      resolver,
      mode: "onBlur",
      defaultValues: normalizedDefaults,
    })

    // Watch selected class template to show price preview
    const selectedClassTemplateId = useWatch({
      control: form.control,
      name: "class_template_id",
    })

    // Watch for selected session ID
    const selectedSessionId = form.watch("session_id")

    // Find selected class template and get price
    const selectedClassTemplate = useMemo(() => {
      if (!selectedClassTemplateId || !classTemplatesData?.items) return null
      return classTemplatesData.items.find(
        (t) => t.id === selectedClassTemplateId,
      )
    }, [selectedClassTemplateId, classTemplatesData])

    // Find selected session and get total_classes
    const selectedSession = useMemo(() => {
      if (!selectedSessionId || !sessionsData?.items) return null
      return sessionsData.items.find((s) => s.id === selectedSessionId)
    }, [selectedSessionId, sessionsData])

    // Fetch future sessions count for pro-rating
    const { data: futureSessionsData } = useQuery({
      queryKey: [
        "future-sessions-count",
        selectedClassTemplateId,
        selectedSessionId,
      ],
      queryFn: async () => {
        if (!selectedClassTemplateId || !selectedSessionId) return null
        const response =
          await ClassSessionsService.getFutureClassSessionsCountApiV1ClassSessionsFutureCountGet(
            {
              classTemplateId: selectedClassTemplateId,
              sessionId: selectedSessionId,
            },
          )
        return response.data
      },
      enabled: !!selectedClassTemplateId && !!selectedSessionId,
    })

    // Calculate total price: price x remaining_classes (pro-rated)
    const pricingInfo = useMemo(() => {
      if (!selectedClassTemplate?.price || !selectedSession?.total_classes)
        return null

      const pricePerClass = Number(selectedClassTemplate.price)

      // Use future sessions count for pro-rating, fall back to total_classes
      const remainingClasses: number = Number(
        futureSessionsData?.future_count ?? selectedSession.total_classes,
      )
      const totalClasses: number = Number(
        futureSessionsData?.total_count ?? selectedSession.total_classes,
      )
      const total = pricePerClass * remainingClasses

      // Show if pro-rated
      const isProRated = remainingClasses < totalClasses

      return {
        pricePerClass,
        totalClasses,
        remainingClasses,
        total,
        isProRated,
        formatted: `$${pricePerClass.toFixed(2)} × ${remainingClasses} ${isProRated ? "remaining " : ""}classes = $${total.toFixed(2)}`,
      }
    }, [selectedClassTemplate, selectedSession, futureSessionsData])

    const formattedPrice = useMemo(() => {
      if (!pricingInfo) {
        if (selectedClassTemplate?.price) {
          return `$${Number(selectedClassTemplate.price).toFixed(2)}/class (select session for total)`
        }
        return null
      }
      return pricingInfo.formatted
    }, [pricingInfo, selectedClassTemplate])

    useEffect(() => {
      if (isOpen) {
        void form.reset(normalizedDefaults)
      }
    }, [form, isOpen, normalizedDefaults])

    const handleClose = () => {
      form.reset(normalizedDefaults)
      onClose()
    }

    const handleSubmit: SubmitHandler<EnrollmentFormValues> = async (
      values,
    ) => {
      // Convert empty strings to null for optional fields
      const submissionValues = {
        ...values,
        notes: values.notes === "" ? null : values.notes,
      }

      console.log("Submitting enrollment:", submissionValues)
      await onSubmit(submissionValues)
      handleClose()
    }

    return (
      <DialogRoot
        open={isOpen}
        onOpenChange={(details) => {
          if (!details.open) {
            handleClose()
          }
        }}
        size={{ base: "full", md: "md" }}
        trapFocus
        restoreFocus
        initialFocusEl={() => null}
        finalFocusEl={() => finalFocusRef?.current ?? null}
      >
        <Portal>
          <DialogBackdrop />
          <DialogPositioner
            inset="0"
            display="grid"
            placeItems="center"
            padding={{ base: 4, md: 6 }}
          >
            <DialogContent as="form" onSubmit={form.handleSubmit(handleSubmit)} maxH="90vh" display="flex" flexDirection="column">
              {showSkipLink && (
                <a
                  href={`#${formSectionId}`}
                  style={skipLinkStyle}
                  onFocus={(event) => {
                    event.currentTarget.style.left = "16px"
                    event.currentTarget.style.top = "16px"
                  }}
                  onBlur={(event) => {
                    event.currentTarget.style.left = "-9999px"
                    event.currentTarget.style.top = "-9999px"
                  }}
                >
                  Skip to form fields
                </a>
              )}

              <DialogHeader>
                <DialogTitle>{title}</DialogTitle>
                <DialogCloseTrigger />
              </DialogHeader>

              <DialogBody id={formSectionId} overflowY="auto" flex="1">
                <VStack gap={6}>
                  {/* Enrollment Information */}
                  <Box w="full">
                    <Text fontSize="md" fontWeight="semibold" mb={4}>
                      Enrollment Information
                    </Text>
                    <VStack gap={4}>
                      <Field
                        required
                        invalid={!!form.formState.errors.student_id}
                        errorText={form.formState.errors.student_id?.message}
                        label="Student"
                      >
                        <ForeignKeySelect
                          register={form.register("student_id")}
                          options={studentsData?.items}
                          isLoading={studentsLoading}
                          placeholder="Select Student"
                          hasError={!!form.formState.errors.student_id}
                          formatLabel={(s) => `${s.first_name} ${s.last_name}`}
                        />
                      </Field>

                      <Field
                        required
                        invalid={!!form.formState.errors.session_id}
                        errorText={form.formState.errors.session_id?.message}
                        label="Session"
                      >
                        <ForeignKeySelect
                          register={form.register("session_id")}
                          options={sessionsData?.items}
                          isLoading={sessionsLoading}
                          placeholder="Select Session"
                          hasError={!!form.formState.errors.session_id}
                          formatLabel={(s) =>
                            `${s.name} - ${s.total_classes} classes`
                          }
                        />
                      </Field>

                      <Field
                        required
                        invalid={!!form.formState.errors.class_template_id}
                        errorText={
                          form.formState.errors.class_template_id?.message
                        }
                        label="Class Template"
                      >
                        <ForeignKeySelect
                          register={form.register("class_template_id")}
                          options={classTemplatesData?.items}
                          isLoading={classTemplatesLoading}
                          placeholder="Select Class Template"
                          hasError={!!form.formState.errors.class_template_id}
                          formatLabel={(t) => {
                            const current = t.current_enrollment ?? 0
                            const max = t.max_students ?? 0
                            const spotsLeft = max - current

                            let availability: string
                            if (spotsLeft > 0) {
                              availability = `${spotsLeft}/${max} spots`
                            } else if (t.waitlist_enabled) {
                              availability = "FULL (waitlist available)"
                            } else {
                              availability = "FULL"
                            }

                            return `${t.name} (${t.day_of_week} ${t.start_time}) - ${availability}`
                          }}
                        />
                      </Field>

                      {formattedPrice && (
                        <Box
                          p={4}
                          bg={pricingInfo?.isProRated ? "orange.50" : "blue.50"}
                          borderRadius="md"
                          borderWidth="1px"
                          borderColor={
                            pricingInfo?.isProRated ? "orange.200" : "blue.200"
                          }
                          w="full"
                        >
                          <Text fontSize="sm" color="fg.muted" mb={1}>
                            {pricingInfo?.isProRated
                              ? "Pro-Rated Class Fee (Mid-Session Enrollment)"
                              : "Total Class Fee"}
                          </Text>
                          <Text
                            fontSize="2xl"
                            fontWeight="bold"
                            color={
                              pricingInfo?.isProRated
                                ? "orange.600"
                                : "blue.600"
                            }
                          >
                            {formattedPrice}
                          </Text>
                          <Text fontSize="xs" color="fg.subtle" mt={1}>
                            {pricingInfo?.isProRated
                              ? `Enrolling mid-session: ${pricingInfo.remainingClasses} of ${pricingInfo.totalClasses} classes remaining`
                              : "An invoice will be created with this amount. Payment can be made later."}
                          </Text>
                        </Box>
                      )}

                      <Field
                        invalid={!!form.formState.errors.notes}
                        errorText={form.formState.errors.notes?.message}
                        label="Enrollment Notes"
                      >
                        <Textarea
                          {...form.register("notes")}
                          placeholder={"Enter enrollment notes"}
                          rows={2}
                          resize="vertical"
                          aria-invalid={!!form.formState.errors.notes}
                        />
                      </Field>
                    </VStack>
                  </Box>
                </VStack>
              </DialogBody>

              <DialogFooter>
                <HStack gap={3}>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    {cancelLabel}
                  </Button>
                  <Button
                    type="submit"
                    colorPalette="blue"
                    loading={isSubmitting}
                    loadingText={loadingText ?? `${submitLabel}...`}
                  >
                    {submitLabel}
                  </Button>
                </HStack>
              </DialogFooter>
            </DialogContent>
          </DialogPositioner>
        </Portal>
      </DialogRoot>
    )
  },
)

EnrollmentFormDialog.displayName = "EnrollmentFormDialog"

export default EnrollmentFormDialog
